import {
  afterNextRender,
  AfterContentChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  ViewChild,
} from "@angular/core";

import { GlossScrollDirective } from "./gloss-scroll.directive";

@Component({
  selector: "app-gloss-scroll",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [GlossScrollDirective],
  templateUrl: "./gloss-scroll.component.html",
  host: { class: "tw-relative tw-block" },
})
// A scroll area with GlossScroll's overlay bar (GlossScrollDirective) that caps its height at `rows`
// rows: projected content must mark each row with `[data-gs-row]`, and the rows' parent supplies
// the bottom padding (kept visible below the last row, so shadows aren't cut).
export class GlossScrollComponent implements OnDestroy, AfterContentChecked {
  readonly rows = input<number | null>(null);
  // Upper bound on the height in px, whatever the rows measure (e.g. a parent that clips there).
  readonly maxHeight = input<number | null>(null);
  // Accessible name for the scroll region; only applied (with role="region") when set.
  readonly label = input<string | null>(null);

  @ViewChild("viewport", { static: true }) private viewport!: ElementRef<HTMLElement>;
  @ViewChild(GlossScrollDirective, { static: true }) readonly scrollbar!: GlossScrollDirective;

  private zone = inject(NgZone);
  private lastRowKey = "";
  private cleanup = () => {};

  constructor() {
    afterNextRender(() => this.zone.runOutsideAngular(() => this.observeRows()));
  }

  // Apply the cap in the same change-detection pass that renders the rows (not a ResizeObserver
  // frame later), so a consumer measuring the height in afterNextRender sees the final size.
  ngAfterContentChecked(): void {
    const count = this.viewport.nativeElement.querySelectorAll("[data-gs-row]").length;
    const key = `${this.rows()}:${this.maxHeight()}:${count}`;
    if (key !== this.lastRowKey) {
      this.lastRowKey = key;
      this.applyMaxHeight();
    }
  }

  scrollToTop(): void {
    this.viewport.nativeElement.scrollTop = 0;
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  // Rows change height (fonts, wrapping, entrance animations ending), which moves the cap.
  private observeRows(): void {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => this.applyMaxHeight());
    observer.observe(this.viewport.nativeElement.firstElementChild!);
    this.cleanup = () => observer.disconnect();
  }

  private applyMaxHeight(): void {
    const el = this.viewport.nativeElement;
    const rows = this.rows();
    const items = el.querySelectorAll<HTMLElement>("[data-gs-row]");
    let cap = this.maxHeight() ?? Infinity;
    if (rows && items.length > rows) {
      // offsetTop is relative to the viewport (it is the positioned offsetParent) and, unlike
      // getBoundingClientRect, ignores the rows' entrance transforms.
      const last = items[rows - 1];
      const padding = parseFloat(getComputedStyle(last.parentElement!).paddingBottom) || 0;
      cap = Math.min(cap, last.offsetTop + last.offsetHeight + padding);
    }
    const maxHeight = cap === Infinity ? "" : `${cap}px`;
    if (el.style.maxHeight !== maxHeight) {
      el.style.maxHeight = maxHeight;
    }
  }
}
